// utils/chatUnread.js
//
// Pure helpers for the Chats tab's iMessage-style unread cues (WP-U). Rows are
// the chat row objects built by utils/chatListGrouping.js; a row is unread when
// `row.hasUnread` is truthy. All counts are in CHATS, not messages. Archived
// rows are not part of the grouped pane lists and are never counted.

const hasUnread = (row) => !!row && !!row.hasUnread;

export function countUnread(rows) {
  return (rows || []).reduce((n, r) => n + (hasUnread(r) ? 1 : 0), 0);
}

export function anyUnread(rows) {
  return (rows || []).some(hasUnread);
}

// Sub-chats nested under a club row (empty for non-club rows).
export function rowSubs(grouped, chat) {
  if (!chat || chat.kind !== "club") return [];
  return (grouped.subchatsByClubId && grouped.subchatsByClubId[chat.club_id]) || [];
}

// Unread chats contributed by one pane entry: the row itself plus its subs.
export function entryUnreadCount(chat, subs) {
  return (hasUnread(chat) ? 1 : 0) + countUnread(subs);
}

// Unpinned club entries, then orphan sub-chats (subs = []), in render order.
export function clubListEntries(grouped) {
  return [
    ...grouped.unpinnedClubs.map((chat) => ({ chat, subs: rowSubs(grouped, chat) })),
    ...grouped.orphanSubchats.map((chat) => ({ chat, subs: [] })),
  ];
}

// Entries for the pinned section (clubs carry their subs; events none).
export function pinnedEntries(grouped) {
  return grouped.pinned.map((chat) => ({ chat, subs: rowSubs(grouped, chat) }));
}

// Number of unread chats shown under the Clubs segment: pinned, unpinned clubs,
// nested + orphan sub-chats, and event chats.
export function clubsSegmentUnreadCount(grouped) {
  const entries = [...pinnedEntries(grouped), ...clubListEntries(grouped)];
  const entryTotal = entries.reduce((n, e) => n + entryUnreadCount(e.chat, e.subs), 0);
  return entryTotal + countUnread(grouped.unpinnedEvents);
}

// Unread chats among the entries hidden behind "View all chats".
export function hiddenUnreadCount(entries, collapsedCount) {
  return entries
    .slice(collapsedCount)
    .reduce((n, e) => n + entryUnreadCount(e.chat, e.subs), 0);
}

// Unread-filter view of one entry: whether the club row itself stays, and
// which subs stay. An entry with nothing unread has showRow=false, subs=[].
export function unreadOnlyEntry(chat, subs) {
  return { showRow: hasUnread(chat), subs: (subs || []).filter(hasUnread) };
}

// Distinct chats with unread, excluding the chat currently open.
export function otherUnreadChatCount(unreadChatIds, currentChatId, currentChatType) {
  // The E-Board room's own unread key is `<chatId>#eboard`.
  const current = isEboardChatType(currentChatType) && currentChatId ? eboardKey(currentChatId) : currentChatId;
  let n = 0;
  for (const id of unreadChatIds || []) if (id && id !== current) n += 1;
  return n;
}

// ── Notification -> chat key (single source; used by UnreadChatsContext) ───────
// `data.type` values written by the chat services' send* methods.
const CHAT_NOTIFICATION_TYPES = new Set([
  "club_message",
  "event_message",
  "subchat_message",
  "dm_message",
  "dm_request",
]);

const EBOARD_KEY_SUFFIX = "#eboard";
export const eboardKey = (chatId) => `${chatId}${EBOARD_KEY_SUFFIX}`;
// The chat id inside an E-Board key, or null for any other key.
export const eboardBaseId = (key) =>
  typeof key === "string" && key.endsWith(EBOARD_KEY_SUFFIX) ? key.slice(0, -EBOARD_KEY_SUFFIX.length) : null;
// The restricted room is opened with chatType "internal" ("eboard" from legacy callers).
export const isEboardChatType = (chatType) => chatType === "internal" || chatType === "eboard";
// Room a club_message notification opens: E-Board messages are tagged
// chat_type "internal" (older ones are not); anything else is the general room.
export const clubChatTypeFromNotification = (data) =>
  data && data.chat_type === "internal" ? "internal" : "general";

// Maps a chat-message notification to {kind, chatId} (the local read-marker key),
// or null for anything that is not a chat message. A club_message sent in the
// restricted room (data.chat_type === "internal") gets its own `<chatId>#eboard`
// key; older notifications without chat_type keep mapping to the general chat.
export function unreadKeyFor(notification) {
  const data = (notification && notification.data) || {};
  if (!CHAT_NOTIFICATION_TYPES.has(data.type)) return null;
  if (data.type === "subchat_message" && data.subchat_id) {
    return { kind: "subchat", chatId: data.subchat_id };
  }
  if (data.type === "event_message" && data.chat_id) {
    return { kind: "event", chatId: data.chat_id };
  }
  if (data.type === "club_message" && data.chat_id) {
    if (data.chat_type === "internal") {
      return { kind: "eboard", chatId: eboardKey(data.chat_id) };
    }
    return { kind: "club", chatId: data.chat_id };
  }
  if (
    (data.type === "dm_message" || data.type === "dm_request") &&
    data.conversation_id
  ) {
    return { kind: "dm", chatId: data.conversation_id };
  }
  return null;
}

// Unread notices that pre-date the E-Board tag (no data.chat_type, so they key
// as the general chat) but whose message_id is one of the E-Board room's
// messages: opening that room has read them.
export function legacyEboardNoticeIds(notices, chatId, messageIds) {
  const ids = messageIds instanceof Set ? messageIds : new Set(messageIds || []);
  if (!chatId || ids.size === 0) return [];
  return (notices || [])
    .filter((n) => {
      const key = unreadKeyFor(n);
      return key && key.kind === "club" && key.chatId === chatId && n.id && ids.has(n.data?.message_id);
    })
    .map((n) => n.id);
}

// DM request ids that are the note of a pending incoming connection from the
// same person: one request, counted once (as the connection).
export function standaloneDmRequestIds(dmRequests, incomingFromUids) {
  const from = incomingFromUids instanceof Set ? incomingFromUids : new Set(incomingFromUids || []);
  // Only a thread the server marked as a connection note is counted with its
  // connection; an independent DM request from the same person counts on its own.
  return (dmRequests || []).filter((r) => r && !(r.connection_note === true && from.has(r.otherUid))).map((r) => r.id);
}

// Ids of chat-message notifications whose chat is not in `knownChatIds` (an
// iterable of chat ids / `#eboard` keys): nothing on screen can ever clear
// them. Capped at `limit` per call.
export function orphanUnreadNotificationIds(notices, knownChatIds, limit = 50) {
  const known = knownChatIds instanceof Set ? knownChatIds : new Set(knownChatIds || []);
  const ids = [];
  for (const n of notices || []) {
    if (ids.length >= limit) break;
    const key = unreadKeyFor(n);
    if (key && n.id && !known.has(key.chatId)) ids.push(n.id);
  }
  return ids;
}

// True when the viewer leads this club chat row (admin or e-board), i.e. the
// club's E-Board ("internal") room is visible to them. Shared by the mobile
// Chats screen, useEboardUnread, sweepOrphanUnread and the web chat list.
export function isInternalChatRow(chat) {
  return chat.member_role === "admin" || chat.member_role === "e-board";
}
